import React, { ChangeEvent, FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import DayjsUtils from '@date-io/dayjs';

import MaterialTable, { EditComponentProps } from 'material-table';
import { IconButton, Link, Paper } from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import PlayCircle from '@material-ui/icons/PlayCircleFilled';

import ChannelListDropdown from '../channels/ChannelListDropdown';
import StatusChip, { statusChipStyles } from '../common/StatusChip';
import { useLanguageContext } from '../languages';
import { getLocalTimezone } from '../../utils/dateTransform';
import { Fer, LoadingOptions } from '../../utils/types';
import { HarvestFer } from './FersEditor';

export const Fers: FC<FersProps> = (props) => {
	const { onRowAdd, onRowDelete, fers, gameStartTime, sendToHarvest, harvestDays } = props;

	const statusChipClasses = statusChipStyles();

	const { tableLocalization } = useLanguageContext();

	const canHarvest = (row: Fer): boolean => {
		const now = dayjs();
		const start = dayjs(row.startTime);
		return now.isAfter(start) && now.diff(start, 'day') <= harvestDays;
	};

	return (
		<div style={{ maxWidth: '100%' }}>
			<MaterialTable
				localization={tableLocalization}
				columns={[
					{
						title: 'FER ID',
						field: 'ferId',
						editable: 'never',
						render: (row: Fer): JSX.Element =>
							row.available ? (
								<Link component={RouterLink} to={`/games/${row.gameId}/fers/${row.ferId}`} color='secondary'>
									{row.ferId}
								</Link>
							) : (
								<span>{row.ferId}</span>
							)
					},
					{
						title: 'Status',
						field: 'available',
						editable: 'never',
						align: 'center',
						render: (row: Fer): JSX.Element => (
							<StatusChip className={statusChipClasses.mediumChip} status={row.available ? 'AVAILABLE' : 'NOT_AVAILABLE'} />
						)
					},
					{
						title: 'Channel ID',
						field: 'channelId',
						editComponent: (props: EditComponentProps<Fer>): JSX.Element => {
							const handleChannelChange = (event: ChangeEvent<HTMLInputElement>): void => {
								props.onChange(event.target.value);
							};
							return <ChannelListDropdown hideLabel={true} channelId={props.value ?? null} onSelectChange={handleChannelChange} />;
						}
					},
					{
						title: 'Channel Territory',
						field: 'channel.territory',
						editable: 'never'
					},
					{
						title: `Start time (${getLocalTimezone()})`,
						field: 'startTime',
						align: 'center',
						render: (row: Fer): JSX.Element => (
							<span>
								{row.startTime && dayjs(row.startTime).isValid() ? (
									<>
										<strong>{dayjs(row.startTime).format('DD MMM YYYY')}</strong> {dayjs(row.startTime).format('HH:mm')}
									</>
								) : (
									'Unknown'
								)}
							</span>
						),
						editComponent: (props: EditComponentProps<Fer>): JSX.Element => (
							<MuiPickersUtilsProvider utils={DayjsUtils}>
								<DateTimePicker
									id='startTime'
									name='startTime'
									value={props.value}
									onChange={(date: dayjs.Dayjs | null): void => props.onChange(date?.toISOString())}
									InputProps={{
										style: { fontSize: '0.8rem' }
									}}
								/>
							</MuiPickersUtilsProvider>
						),
						initialEditValue: gameStartTime ? dayjs(gameStartTime) : dayjs()
					},
					{
						title: `End time (${getLocalTimezone()})`,
						field: 'endTime',
						align: 'center',
						editable: 'never',
						render: (row: Fer): JSX.Element => (
							<span>
								{row.endTime && dayjs(row.endTime).isValid() ? (
									<>
										<strong>{dayjs(row.endTime).format('DD MMM YYYY')}</strong> {dayjs(row.endTime).format('HH:mm')}
									</>
								) : (
									'Nil'
								)}
							</span>
						)
					},
					{
						title: 'Harvest',
						field: 'ferId',
						align: 'center',
						editable: 'never',
						render: (row: Fer): JSX.Element => (
							<IconButton
								aria-label='Send to Harvest'
								onClick={(): void => sendToHarvest({ ferId: row.ferId, startTime: row.startTime, channelId: row.channelId })}
								disabled={!canHarvest(row)}>
								<PlayCircle />
							</IconButton>
						)
					}
				]}
				editable={{ onRowAdd, onRowDelete, isDeletable: (): boolean => true }}
				data={fers}
				options={{ paging: false, search: false, sorting: false, emptyRowsWhenPaging: false }}
				title='Full Event Replays'
				components={{
					Container: (props): JSX.Element => <Paper {...props} data-testid='fers-table' />
				}}
			/>
		</div>
	);
};

type FersProps = {
	fers: Fer[];
	onRowAdd?: (newData: Fer) => Promise<unknown>;
	onRowDelete?: (oldData: Fer) => Promise<unknown>;
	onRowUpdate?: (newData: Fer, oldData?: Fer) => Promise<unknown>;
	loadingOptions: LoadingOptions;
	gameStartTime: string | null;
	sendToHarvest: (fer: HarvestFer) => void;
	harvestDays: number;
};

export default Fers;
